import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHero, Card, C, Loader, PrimaryButton } from "@/components/district/kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserPlus, EnvelopeSimple, Phone, IdentificationCard, CircleNotch, Star } from "@phosphor-icons/react";

const TODAY = new Date().toISOString().slice(0, 10);
const BLANK = { full_name: "", rotary_id: "", mobile: "", email: "", gender: "", joined_club: TODAY, classification: "", spouse_name: "", address: "" };

export default function ClubMembers() {
  const params = useParams();
  const navigate = useNavigate();
  const [clubId, setClubId] = useState(null);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [honorary, setHonorary] = useState(null); // { member_name, existing_club }

  const load = async (cid) => { const r = await api.get(`/clubs/${cid}/members/manage`); setData(r.data); };
  useEffect(() => {
    if (params.clubId) { setClubId(params.clubId); load(params.clubId).catch(() => setData({ none: true })); return; }
    api.get("/me/home").then(async (r) => {
      if (r.data.club_id) { setClubId(r.data.club_id); await load(r.data.club_id); }
      else setData({ none: true });
    });
  }, [params.clubId]);
  if (!data) return <Loader label="Loading members…" />;
  if (data.none) return <p className="text-[16px]" style={{ color: C.sub }}>This is available to club officers.</p>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (asHonorary) => {
    if (!form.full_name.trim() || !form.rotary_id.trim() || !form.mobile.trim() || !form.email.trim()) {
      toast.error("Name, Membership ID, mobile and email are required"); return;
    }
    if (!form.gender) { toast.error("Select a gender (used for the membership infographic)"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/clubs/${clubId}/members`, { ...form, add_as_honorary: asHonorary });
      toast.success(r.data.honorary ? "Added as an honorary member" : "Member added to the repository");
      setOpen(false); setHonorary(null); setForm(BLANK); await load(clubId);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "member_in_other_club") {
        setHonorary({ member_name: detail.member_name, existing_club: detail.existing_club });
      } else {
        toast.error((typeof detail === "string" && detail) || "Could not add member");
      }
    } finally {
      setBusy(false);
    }
  };

  const F = [
    ["full_name", "Full name", true, IdentificationCard],
    ["rotary_id", "Membership ID", true, IdentificationCard],
    ["mobile", "Mobile", true, Phone],
    ["email", "Email", true, EnvelopeSimple],
    ["classification", "Classification", false, null],
    ["spouse_name", "Spouse name", false, null],
    ["address", "Address", false, null],
  ];

  return (
    <div className="mx-auto w-full max-w-[900px] space-y-6" data-testid="club-members-page">
      <button data-testid="back-to-club-dashboard" onClick={() => navigate(params.clubId ? `/club/${params.clubId}` : "/")}
        className="inline-flex items-center gap-1.5 text-[14px] font-semibold" style={{ color: "#4B31C9" }}>
        <span aria-hidden>←</span> Back to club dashboard
      </button>
      <PageHero title="Club members" subtitle="Your membership repository — built by the district at the start of the year."
        stats={[{ value: data.count, label: "Members" }]} />

      {data.can_manage && (
        <PrimaryButton data-testid="open-add-member" onClick={() => setOpen(true)}>
          <UserPlus size={18} weight="bold" /> Add a member
        </PrimaryButton>
      )}

      <Card pad={false}>
        <div className="divide-y" style={{ borderColor: C.line }}>
          {data.members.map((m) => (
            <div key={m.member_id} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3.5" data-testid={`member-${m.member_id}`}>
              <div className="min-w-0">
                <p className="text-[15px] font-semibold flex items-center gap-2" style={{ color: C.ink }}>
                  {m.full_name}
                  {m.honorary && (
                    <span data-testid={`honorary-badge-${m.member_id}`} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] font-semibold" style={{ background: "#FFF4D6", color: "#8A6100" }}>
                      <Star size={11} weight="fill" /> Honorary
                    </span>
                  )}
                  {m.status !== "active" && <span className="ml-1 rounded-full px-2 py-0.5 text-[12px] font-semibold" style={{ background: "#FDECEA", color: "#C8102E" }}>inactive</span>}
                </p>
                <p className="text-[13px]" style={{ color: C.sub }}>
                  {[m.rotary_id, m.mobile, m.email].filter(Boolean).join(" · ") || "No contact details"}
                  {m.honorary && m.honorary_primary_club && <span> · member of {m.honorary_primary_club}</span>}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(m.roles || []).map((r) => (
                  <span key={r} className="rounded-full px-2.5 py-0.5 text-[12px] font-semibold" style={{ background: C.indigoSoft, color: C.indigoText }}>{r}</span>
                ))}
              </div>
            </div>
          ))}
          {data.members.length === 0 && <p className="px-5 py-6 text-[15px]" style={{ color: C.sub }}>No members yet. Add your first member.</p>}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(BLANK); }}>
        <DialogContent data-testid="add-member-dialog">
          <DialogHeader><DialogTitle>Add a member</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {F.map(([k, label, req]) => (
              <label key={k} className={k === "address" ? "sm:col-span-2" : ""}>
                <span className="text-[13px] font-semibold" style={{ color: C.sub }}>{label}{req && " *"}</span>
                <input data-testid={`member-field-${k}`} value={form[k]} onChange={set(k)}
                  className="mt-1 w-full rounded-xl border px-3 py-2.5 text-[15px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line, color: C.ink }} />
              </label>
            ))}
            <label>
              <span className="text-[13px] font-semibold" style={{ color: C.sub }}>Gender *</span>
              <select data-testid="member-field-gender" value={form.gender} onChange={set("gender")}
                className="mt-1 w-full rounded-xl border px-3 py-2.5 text-[15px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line, color: C.ink }}>
                <option value="">Select…</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </label>
            <label>
              <span className="text-[13px] font-semibold" style={{ color: C.sub }}>Member Admit Date *</span>
              <input type="date" data-testid="member-field-joined_club" value={form.joined_club} onChange={set("joined_club")}
                className="mt-1 w-full rounded-xl border px-3 py-2.5 text-[15px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line, color: C.ink }} />
              <span className="mt-0.5 block text-[11px]" style={{ color: C.sub }}>Club admission date — counts them as a new member this Rotary Year.</span>
            </label>
          </div>
          <DialogFooter>
            <PrimaryButton data-testid="confirm-add-member" onClick={() => submit(false)} disabled={busy}>
              {busy ? <CircleNotch size={16} className="animate-spin" /> : <UserPlus size={16} weight="bold" />} Add member
            </PrimaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!honorary} onOpenChange={(v) => { if (!v) setHonorary(null); }}>
        <DialogContent data-testid="honorary-confirm-dialog">
          <DialogHeader><DialogTitle>Member already exists</DialogTitle></DialogHeader>
          <p className="text-[15px]" style={{ color: C.ink }}>
            <strong>{honorary?.member_name}</strong> is already a member of <strong>{honorary?.existing_club}</strong>.
          </p>
          <p className="text-[14px]" style={{ color: C.sub }}>
            Do you want to add this person as an <strong>honorary member</strong> of your club?
          </p>
          <DialogFooter className="gap-2">
            <button data-testid="honorary-cancel" onClick={() => setHonorary(null)}
              className="rounded-xl border px-4 py-2 text-[14px] font-semibold" style={{ borderColor: C.line, color: C.sub }}>
              Cancel
            </button>
            <PrimaryButton data-testid="honorary-confirm" onClick={() => submit(true)} disabled={busy}>
              {busy ? <CircleNotch size={16} className="animate-spin" /> : <Star size={16} weight="fill" />} Add as honorary
            </PrimaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
